import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { SUCCESS_TOAST, ERROR_TOAST } from "../../helpers/toasters";
import { getAttendanceByDate } from "../../API/attendence";
import './ExportModal.css';

interface Student {
    _id: string;
    name: string;
    roll_no?: string; // Updated to roll_no instead of rollNumber
}

interface AttendanceRecord {
    date: string;
    studentId: string;
    present: boolean;
}

interface Batch {
    _id: string;
    name: string;
    batch_id: string[] | string;
}
interface ExportModalProps {
    students: Student[];
    attendanceData: AttendanceRecord[]; // This will be just the current date's data
    selectedBatch: string | null;
    batches: Batch[];
    selectedDate: string;
    onClose: () => void;
}

// Define the structure of our export data
interface ExportData {
    BatchName: string;
    batch_id: string;
    RollNumber: string;
    Name: string;
    Attendance: string;
}

const ExportModal: React.FC<ExportModalProps> = ({
    students,
    attendanceData,
    selectedBatch,
    batches,
    selectedDate,
    onClose
}) => {
    // Initialize with the selectedDate from parent component
    const [startDate, setStartDate] = useState(selectedDate);
    const [endDate, setEndDate] = useState(selectedDate);
    const [isLoading, setIsLoading] = useState(false);
    const [allAttendanceData, setAllAttendanceData] = useState<AttendanceRecord[]>(attendanceData);

    // Update state if selectedDate prop changes
    useEffect(() => {
        setStartDate(selectedDate);
        setEndDate(selectedDate);
    }, [selectedDate]);

    // Update the fetchAttendanceForDateRange function in ExportModal
    const fetchAttendanceForDateRange = async () => {
        if (!selectedBatch) return;

        setIsLoading(true);

        // Start with current day's attendance that we already have
        const allRecords = [...attendanceData];

        try {
            // Calculate the date range, excluding the current date (we already have that data)
            const start = new Date(startDate);
            const end = new Date(endDate);

            // Create an array of dates to fetch (excluding today's date which we already have)
            const datesToFetch: string[] = [];
            const currentDateStr = new Date(selectedDate).toISOString().split("T")[0];

            for (let d = new Date(start); d <= end; d = new Date(d.setDate(d.getDate() + 1))) {
                const dateStr = d.toISOString().split("T")[0];
                // Only fetch dates that aren't the current date
                if (dateStr !== currentDateStr) {
                    datesToFetch.push(dateStr);
                }
            }

            // Fetch attendance for each date
            for (const dateStr of datesToFetch) {
                const response = await getAttendanceByDate(selectedBatch, dateStr);

                if (response.message === "success" && Array.isArray(response.data)) {
                    // Format the response data to match our AttendanceRecord structure
                    const dateRecords = response.data.map((record: any) => ({
                        studentId: record.studentId,
                        present: record.present || false,
                        date: dateStr
                    }));

                    allRecords.push(...dateRecords);
                }
            }

            setAllAttendanceData(allRecords);
        } catch (error) {
            console.error("Error fetching attendance data:", error);
            ERROR_TOAST("Failed to fetch attendance data for some dates");
        } finally {
            setIsLoading(false);
        }
    };

    // When date range changes, fetch the attendance data
    useEffect(() => {
        if (startDate !== selectedDate || endDate !== selectedDate) {
            fetchAttendanceForDateRange();
        }
    }, [startDate, endDate]);

    const handleExport = async () => {
        if (!selectedBatch || !students.length) {
            return ERROR_TOAST("Please select a batch and ensure students are available!");
        }

        // Validate dates
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start > end) {
            return ERROR_TOAST("Start date must be before end date!");
        }

        // If we're exporting a date range and haven't fetched data yet, do it now
        if ((startDate !== selectedDate || endDate !== selectedDate) &&
            allAttendanceData.length === attendanceData.length) {
            setIsLoading(true);
            await fetchAttendanceForDateRange();
            setIsLoading(false);
        }
        const currentBatch = selectedBatch && batches && batches.length > 0
            ? batches.find((b) => b._id === selectedBatch)
            : null;
        const batchName = currentBatch?.name || "Unknown Batch";

        // Fix: Handle null/undefined currentBatch properly in the conditional check
        const batchId = currentBatch && Array.isArray(currentBatch.batch_id) && currentBatch.batch_id.length > 0
            ? String(currentBatch.batch_id[0])  // Convert to string explicitly 
            : (currentBatch && typeof currentBatch.batch_id === 'string' ? currentBatch.batch_id : "Unknown ID");

        const fileName = `Attendance_${batchName}_${startDate}_to_${endDate}.xlsx`;
        
        
        

        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Create a summary sheet with all dates
        const summaryData: { BatchName: string; batch_id: string; RollNumber: string; Name: string;[key: string]: string }[] = [];

        students.forEach((student) => {
            const summaryRow: { BatchName: string; batch_id: string; RollNumber: string; Name: string;[key: string]: string } = {
                BatchName: batchName,
                batch_id: batchId,
                RollNumber: student.roll_no || "N/A",
                Name: student.name
            };

            // For each date, add a column to the summary
            for (let d = new Date(start); d <= end; d = new Date(d.setDate(d.getDate() + 1))) {
                const dateStr = d.toISOString().split("T")[0];
                const formattedDate = new Date(dateStr).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });

                // Find attendance record for this student on this date
                const record = allAttendanceData.find(
                    (record) => {
                        const recordDate = new Date(record.date).toISOString().split("T")[0];
                        return recordDate === dateStr && record.studentId === student._id;
                    }
                );

                const isPresent = record?.present ?? false;
                summaryRow[formattedDate] = isPresent ? "Present" : "Absent";
            }

            summaryData.push(summaryRow);
        });

        // Add the summary sheet
        const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

        // Process each date and create a separate sheet for it
        const uniqueDates = new Set<string>();

        // Get all unique dates from the selected range
        for (let d = new Date(start); d <= end; d = new Date(d.setDate(d.getDate() + 1))) {
            const dateStr = d.toISOString().split("T")[0];
            uniqueDates.add(dateStr);
        }

        // Create a sheet for each date
        uniqueDates.forEach((dateStr) => {
            const dateData: ExportData[] = [];

            students.forEach((student) => {
                // Find attendance record for this student on this date
                const record = allAttendanceData.find(
                    (record) => {
                        const recordDate = new Date(record.date).toISOString().split("T")[0];
                        return recordDate === dateStr && record.studentId === student._id;
                    }
                );

                const isPresent = record?.present ?? false;

                dateData.push({
                    BatchName: batchName,
                    batch_id: batchId,
                    RollNumber: student.roll_no || "N/A",
                    Name: student.name,
                    Attendance: isPresent ? "Present" : "Absent"
                });
            });

            // Format the date for the sheet name
            const formattedDate = new Date(dateStr).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });

            // Add a sheet for this date
            const dateWorksheet = XLSX.utils.json_to_sheet(dateData);
            XLSX.utils.book_append_sheet(workbook, dateWorksheet, formattedDate);
        });

        // Write the workbook to a file
        XLSX.writeFile(workbook, fileName);

        SUCCESS_TOAST("Attendance exported successfully!");
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h3>Export Attendance</h3>
                <div className="export-form">
                    <div className="form-group">
                        <label>Start Date:</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>End Date:</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>

                    <div className="modal-actions">
                        <button
                            className="export-btn"
                            onClick={handleExport}
                            disabled={isLoading}
                        >
                            {isLoading ? "Loading..." : "Export"}
                        </button>
                        <button
                            className="close-btn"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;